'use client';

import type { IAPIPaginationCursorBased } from '@repo/sdk';
import {
  Pagination,
  PaginationContent,
  PaginationItem,
  PaginationNext,
  PaginationPrevious,
} from '@repo/ui';
import { usePathname, useSearchParams } from 'next/navigation';
import type { JSX } from 'react';
import { PageFooter } from '../page-footer';

interface ICursorPaginationProps {
  pagination: IAPIPaginationCursorBased | null | undefined;
}

function buildHref(pathname: string, searchParams: URLSearchParams, cursor?: string): string {
  const params = new URLSearchParams(searchParams.toString());
  if (cursor) {
    params.set('cursor', cursor);
  } else {
    params.delete('cursor');
  }
  const qs = params.toString();
  return qs ? `${pathname}?${qs}` : pathname;
}

export function CursorPagination({ pagination }: ICursorPaginationProps): JSX.Element | null {
  const pathname = usePathname();
  const searchParams = useSearchParams();

  if (!pagination) {
    return null;
  }

  const currentCursor = Number(searchParams.get('cursor') || '0');
  const hasPrev = currentCursor > 0;
  const prevCursor = Math.max(0, currentCursor - pagination.limit);

  return (
    <PageFooter>
      <p className="text-sm text-muted-foreground">{pagination.total} total</p>
      <Pagination className="mx-0 w-auto justify-end">
        <PaginationContent>
          {hasPrev && (
            <PaginationItem>
              <PaginationPrevious
                href={buildHref(
                  pathname,
                  searchParams,
                  prevCursor > 0 ? String(prevCursor) : undefined,
                )}
              />
            </PaginationItem>
          )}
          {pagination.hasMore && pagination.nextCursor && (
            <PaginationItem>
              <PaginationNext href={buildHref(pathname, searchParams, pagination.nextCursor)} />
            </PaginationItem>
          )}
        </PaginationContent>
      </Pagination>
    </PageFooter>
  );
}
